import { currencies } from "./geo.static";

/**
 * Geo Currency Item Object Definition
 * @typedef {Object} GeoCurrencyItem
 * @property {string} id - The unique identifier for the currency
 * @property {string} name - The name of the currency
 * @property {Object} code - The currency codes
 * @property {string} code.short - The currency symbol
 * @property {string} code.long - The ISO 4217 alpha code
 * @property {string} code.numeric - The ISO 4217 numeric code
 * @property {boolean} decimal - Whether the currency uses decimal places
 * @property {boolean} accepted - Whether the currency is accepted for payments
 * @property {boolean} common - Indicates if the currency is commonly used
 */

/**
 * Create Geo Currency Item Object
 * @param {Object} params
 * @param {string} params.id
 * @param {string} params.name
 * @param {Object} params.code
 * @param {string} params.code.short
 * @param {string} params.code.long
 * @param {string} params.code.numeric
 * @param {boolean} params.decimal
 * @param {boolean} params.accepted
 * @param {boolean} params.common
 * @returns {GeoCurrencyItem}
 */
export function createGeoCurrencyItem({
  id = "usd",
  name = "United States Dollar",
  code = { short: "$", long: "usd", numeric: "840" },
  decimal = true,
  accepted = true,
  common = true,
}) {
  return {
    id,
    name,
    code,
    decimal,
    accepted,
    common,
  };
}

/** Get Geo Currency Item
 * @param {string} [shortCode] - The currency symbol
 * @param {string} [longCode] - The ISO 4217 alpha code
 * @param {string} [numericCode] - The ISO 4217 numeric code
 * @returns {GeoCurrencyItem} - returns usd currency if no code provided or no match found
 */
export function createGeoCurrencyItemByCode({
  shortCode,
  longCode,
  numericCode,
}) {
  const currencyData = currencies.find((currency) => {
    if (
      shortCode &&
      currency.code.short.toLowerCase() === shortCode.toLowerCase()
    )
      return true;
    if (longCode && currency.code.long.toLowerCase() === longCode.toLowerCase())
      return true;
    if (numericCode && currency.code.numeric === numericCode) return true;
    return false;
  });
  return currencyData
    ? createGeoCurrencyItem(currencyData)
    : createGeoCurrencyItem({});
}

/**
 * Geo Currency List Object Definition
 * @typedef {Object} GeoCurrencyList
 * @property {GeoCurrencyItem[]} Currencies - An array of geo currency items
 */

/**
 * Create Geo Currency List Object
 * @returns {GeoCurrencyList} The created Geo Currency List Object
 */
export function createGeoCurrencyList(
  sort = { common: true, id: false, name: false },
  filter = { accepted: true },
) {
  let currencyList = currencies.map((currencyData) =>
    createGeoCurrencyItem(currencyData),
  );

  if (filter.accepted) {
    currencyList = currencyList.filter((currency) => currency.accepted);
  }
  if (sort.common) {
    currencyList.sort((a, b) => {
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return 0;
    });
  }
  if (sort.id) {
    currencyList.sort((a, b) => a.code.short.localeCompare(b.code.short));
  }
  if (sort.name) {
    currencyList.sort((a, b) => a.name.localeCompare(b.name));
  }

  return currencyList;
}
